import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import TimesheetGrid from '../timesheets/timesheet-grid';
import HealthReportForm from '../../components/reporting/health-report-form';
import ForecastVsActual from '../../components/forecast-vs-actual';
import Index from '../index';
import { ProjectRow } from '../../../lib/types';

interface PMDashboardMetrics {
  assignedProjects: ProjectRow[];
  totalProjects: number;
  activeProjects: number;
  hoursLogged: number;
  pendingHealthReports: number;
}

export default function PMHomepage() {
  const [metrics, setMetrics] = useState<PMDashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [showHealthReport, setShowHealthReport] = useState(false);
  const [showPortfolio, setShowPortfolio] = useState(false);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      // Get current user - need to pass email from localStorage
      const authEmail = localStorage.getItem('auth_email');
      if (!authEmail) {
        throw new Error('Not logged in');
      }

      const userRes = await fetch(`/users/me?email=${encodeURIComponent(authEmail)}`);
      const userData = await userRes.json();

      if (!userData.success || !userData.data.Email) {
        throw new Error('Could not fetch user');
      }

      // Get all projects and filter to those assigned to this PM
      const projectsRes = await fetch('/projects');
      const projectsData = await projectsRes.json();
      const allProjects = projectsData.data || [];

      // Filter to projects where current user is the ProjectManager
      const pmEmail = userData.data.Email;
      const assignedProjects = allProjects.filter(
        (p: ProjectRow) => p.ProjectManagerEmail === pmEmail
      );
      const activeProjects = allProjects.filter((p: ProjectRow) => p.Status === 'InDelivery').length;

      setMetrics({
        assignedProjects,
        totalProjects: allProjects.length,
        activeProjects,
        hoursLogged: 0, // TODO: Calculate from timesheets
        pendingHealthReports: 0, // TODO: Calculate from health reports
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load PM dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading PM dashboard...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div style={{ padding: '2rem' }}>
          <h1>PM Dashboard</h1>
          <div className="error">{error}</div>
          <button onClick={() => window.location.reload()} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="container">
        <div style={{ padding: '2rem' }}>
          <h1>PM Dashboard</h1>
          <div className="error">Failed to load metrics. Please try again.</div>
          <button onClick={() => window.location.reload()} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (showPortfolio) {
    return (
      <div>
        <Index />
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <button className="btn-secondary" onClick={() => setShowPortfolio(false)}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 20px' }}>
      {/* Action Buttons */}
      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
        <button
          onClick={() => setShowTimesheet(true)}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          + Submit Timesheet
        </button>
        <button
          onClick={() => setShowHealthReport(true)}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#06b6d4',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          📋 Submit Health Report
        </button>
      </div>

      {/* Metrics Section */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Total Projects</div>
          <div className="metric-value">{metrics.totalProjects}</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Active Projects</div>
          <div className="metric-value">{metrics.activeProjects}</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Hours Logged</div>
          <div className="metric-value">{metrics.hoursLogged}h</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Pending Health Reports</div>
          <div className="metric-value">{metrics.pendingHealthReports}</div>
        </div>
      </div>

      {/* Assigned Projects Section */}
      <div className="dashboard-section">
        <h2>Your Assigned Projects</h2>
        {metrics.assignedProjects.length > 0 ? (
          <div className="projects-table">
            <table>
              <thead>
                <tr>
                  <th>Project ID</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Domain</th>
                  <th>Work Type</th>
                </tr>
              </thead>
              <tbody>
                {metrics.assignedProjects.map(project => (
                  <tr key={project.ProjectId}>
                    <td>{project.ProjectId}</td>
                    <td>{project.Title}</td>
                    <td>{project.Status}</td>
                    <td>{project.Domain}</td>
                    <td>{project.WorkType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No projects assigned yet.</p>
        )}
      </div>

      {/* Forecast vs Actuals Section */}
      <div className="dashboard-section" style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
      }}>
        <ForecastVsActual title="Forecast vs Actuals" />
      </div>


      {showTimesheet && (
        <TimesheetGrid
          onSuccess={() => {
            setShowTimesheet(false);
            alert('Timesheets submitted successfully!');
          }}
          onCancel={() => setShowTimesheet(false)}
        />
      )}

      {showHealthReport && (
        <HealthReportForm
          onSuccess={() => {
            setShowHealthReport(false);
            alert('Health report submitted!');
          }}
          onCancel={() => setShowHealthReport(false)}
        />
      )}
    </div>
  );
}
